import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { MmkCategory, MmkItem } from "../../../types/mmk";
import type { PersonalMmkFormPayload } from "../../../helpers/mmkPersonal";
import {
  MAX_PERSONAL_MMK_CARDS,
  MAX_PERSONAL_CARD_ANSWER_LENGTH,
  MAX_PERSONAL_CARD_FULL_ANSWER_LENGTH,
  MAX_PERSONAL_CARD_QUESTION_LENGTH,
  clampPersonalCardAnswer,
  clampPersonalCardFullAnswer,
  clampPersonalCardQuestion,
} from "../../../helpers/mmkPersonal";
import { CharacterCountHelperRow } from "./character-count-helper-row";
import { CARD_CATEGORIES } from "./constants";
import { focusPanelTextField } from "./focus-panel-text-field";
import { FlashcardPanel } from "./flashcard-panel";
import {
  cancelButtonSx,
  categoryFormControlSx,
  categoryOtherButtonSx,
  categoryRowSx,
  categorySelectMenuProps,
  categorySelectSx,
  dialogActionsSx,
  dialogContentSx,
  dialogPaperSx,
  dialogRootSlotSx,
  dialogTitleSx,
  fieldLabelSx,
  formHelperTextSlotSx,
  personalCardsHintSx,
  primarySubmitButtonSx,
  requiredFieldMarkerSx,
  textFieldEditIconSx,
  textFieldEndAdornmentSx,
  textFieldInputRootSx,
  textFieldOutlineOverflowSx,
} from "./styles";

const NO_CATEGORY_VALUE = "__mmk_no_category__" as const;

type FormState = PersonalMmkFormPayload;

const emptyForm: FormState = {
  question: "",
  quickAnswer: "",
  fullApprovedAnswer: "",
  category: null,
};

export type MmkPersonalCardDialogMode = "add" | "edit";

interface MmkPersonalCardDialogProps {
  open: boolean;
  mode: MmkPersonalCardDialogMode;
  initialItem: MmkItem | null;
  onClose: () => void;
  personalCardsUsed: number;
  onSave: (
    payload: PersonalMmkFormPayload,
  ) => void | boolean | Promise<void | boolean>;
}

export const MmkPersonalCardDialog: React.FC<MmkPersonalCardDialogProps> = ({
  open,
  mode,
  initialItem,
  onClose,
  personalCardsUsed,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [form, setForm] = useState<FormState>(emptyForm);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [questionFocused, setQuestionFocused] = useState(false);
  const [answerFocused, setAnswerFocused] = useState(false);
  const questionInputRef = useRef<HTMLTextAreaElement | null>(null);
  const quickAnswerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const fullAnswerInputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuestionFocused(false);
    setAnswerFocused(false);
    if (mode === "edit" && initialItem) {
      setForm({
        question: clampPersonalCardQuestion(
          (
            initialItem.question ||
            initialItem.title ||
            initialItem.content ||
            ""
          ).trim(),
        ),
        quickAnswer: clampPersonalCardAnswer(
          (initialItem.short_answer || "").trim(),
        ),
        fullApprovedAnswer: clampPersonalCardFullAnswer(
          (initialItem.approved_answer || "").trim(),
        ),
        category: initialItem.category ?? null,
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, mode, initialItem]);

  const handleSubmit = async () => {
    const q = form.question.trim();
    const quick = form.quickAnswer.trim();
    const full = form.fullApprovedAnswer.trim();
    if (!q || !quick || !full) return;
    const keepOpen = await Promise.resolve(
      onSave({
        question: q,
        quickAnswer: quick,
        fullApprovedAnswer: full,
        category: form.category,
      }),
    );
    if (keepOpen !== false) onClose();
  };

  const primaryButtonSx = primarySubmitButtonSx(theme);
  const fieldSize = isMobile ? "medium" : "small";
  const canSubmit =
    !!form.question.trim() &&
    !!form.quickAnswer.trim() &&
    !!form.fullApprovedAnswer.trim();

  const pencilAdornment = (
    <InputAdornment position="end" sx={textFieldEndAdornmentSx}>
      <EditOutlinedIcon
        fontSize="small"
        sx={textFieldEditIconSx}
        aria-hidden
      />
    </InputAdornment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        root: { sx: dialogRootSlotSx },
        paper: { sx: dialogPaperSx },
      }}
    >
      <DialogTitle sx={dialogTitleSx}>
        {mode === "add" ? "New flashcard" : "Edit flashcard"}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <FlashcardPanel
          sectionTitle="Card front"
          highlighted={questionFocused}
          onBodyMouseDown={(e) => focusPanelTextField(e, questionInputRef)}
        >
          <Typography variant="body2" sx={fieldLabelSx}>
            Question:
            <Box component="span" sx={requiredFieldMarkerSx} aria-hidden>
              {" *"}
            </Box>
          </Typography>
          <TextField
            value={form.question}
            inputRef={questionInputRef}
            onFocus={() => setQuestionFocused(true)}
            onBlur={() => setQuestionFocused(false)}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                question: clampPersonalCardQuestion(e.target.value),
              }))
            }
            fullWidth
            required
            multiline
            minRows={2}
            maxRows={isMobile ? 7 : 12}
            variant="outlined"
            size={fieldSize}
            hiddenLabel
            placeholder="Enter your question"
            sx={textFieldOutlineOverflowSx}
            inputProps={{
              maxLength: MAX_PERSONAL_CARD_QUESTION_LENGTH,
              "aria-required": true,
            }}
            InputProps={{
              sx: textFieldInputRootSx,
              endAdornment: pencilAdornment,
            }}
            FormHelperTextProps={{ sx: formHelperTextSlotSx }}
            helperText={
              <CharacterCountHelperRow
                max={MAX_PERSONAL_CARD_QUESTION_LENGTH}
                length={form.question.length}
                locationHint="Visible to you during study."
              />
            }
          />
        </FlashcardPanel>

        <FlashcardPanel
          sectionTitle="Card back"
          highlighted={answerFocused}
          onBodyMouseDown={(e) => focusPanelTextField(e, quickAnswerInputRef)}
        >
          <Typography variant="body2" sx={fieldLabelSx}>
            Quick Answer:
            <Box component="span" sx={requiredFieldMarkerSx} aria-hidden>
              {" *"}
            </Box>
          </Typography>
          <TextField
            value={form.quickAnswer}
            inputRef={quickAnswerInputRef}
            onFocus={() => setAnswerFocused(true)}
            onBlur={() => setAnswerFocused(false)}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                quickAnswer: clampPersonalCardAnswer(e.target.value),
              }))
            }
            fullWidth
            required
            multiline
            minRows={2}
            maxRows={isMobile ? 6 : 8}
            variant="outlined"
            size={fieldSize}
            hiddenLabel
            placeholder="Tap to enter the quick answer..."
            sx={textFieldOutlineOverflowSx}
            inputProps={{
              maxLength: MAX_PERSONAL_CARD_ANSWER_LENGTH,
              "aria-required": true,
            }}
            InputProps={{
              sx: textFieldInputRootSx,
              endAdornment: pencilAdornment,
            }}
            FormHelperTextProps={{ sx: formHelperTextSlotSx }}
            helperText={
              <CharacterCountHelperRow
                max={MAX_PERSONAL_CARD_ANSWER_LENGTH}
                length={form.quickAnswer.length}
                locationHint="Shown first after flipping the card."
              />
            }
          />

          <Typography variant="body2" sx={{ ...fieldLabelSx, mt: 2 }}>
            Full Approved Answer:
            <Box component="span" sx={requiredFieldMarkerSx} aria-hidden>
              {" *"}
            </Box>
          </Typography>
          <TextField
            value={form.fullApprovedAnswer}
            inputRef={fullAnswerInputRef}
            onFocus={() => setAnswerFocused(true)}
            onBlur={() => setAnswerFocused(false)}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                fullApprovedAnswer: clampPersonalCardFullAnswer(e.target.value),
              }))
            }
            fullWidth
            required
            multiline
            minRows={isMobile ? 3 : 4}
            maxRows={isMobile ? 9 : 14}
            variant="outlined"
            size={fieldSize}
            hiddenLabel
            placeholder="Tap to enter the full approved answer..."
            sx={textFieldOutlineOverflowSx}
            inputProps={{
              maxLength: MAX_PERSONAL_CARD_FULL_ANSWER_LENGTH,
              "aria-required": true,
            }}
            InputProps={{
              sx: textFieldInputRootSx,
              endAdornment: pencilAdornment,
            }}
            FormHelperTextProps={{ sx: formHelperTextSlotSx }}
            helperText={
              <CharacterCountHelperRow
                max={MAX_PERSONAL_CARD_FULL_ANSWER_LENGTH}
                length={form.fullApprovedAnswer.length}
                locationHint="Expandable full answer after flipping."
              />
            }
          />
        </FlashcardPanel>

        <Box>
          <Typography variant="body2" sx={fieldLabelSx}>
            Category:
          </Typography>
          <Box sx={categoryRowSx}>
            <FormControl
              size={fieldSize}
              variant="outlined"
              sx={categoryFormControlSx}
            >
              <Select
                variant="outlined"
                open={categoryOpen}
                onOpen={() => setCategoryOpen(true)}
                onClose={() => setCategoryOpen(false)}
                displayEmpty
                value={
                  form.category === null ? NO_CATEGORY_VALUE : form.category
                }
                MenuProps={categorySelectMenuProps}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({
                    ...f,
                    category:
                      v === NO_CATEGORY_VALUE ? null : (v as MmkCategory),
                  }));
                }}
                sx={categorySelectSx}
                renderValue={(selected) =>
                  selected === NO_CATEGORY_VALUE ? (
                    <Typography component="span" color="text.primary">
                      Uncategorized
                    </Typography>
                  ) : (
                    selected
                  )
                }
              >
                <MenuItem value={NO_CATEGORY_VALUE}>Uncategorized</MenuItem>
                {CARD_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size={fieldSize}
              onClick={() => setCategoryOpen(true)}
              sx={categoryOtherButtonSx}
            >
              Other
            </Button>
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={personalCardsHintSx}
        >
          Personal cards used {personalCardsUsed}/{MAX_PERSONAL_MMK_CARDS}
        </Typography>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={primaryButtonSx}
        >
          {mode === "add" ? "Add card" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
